// Age-tiered "key learning objectives" — a curated must-learn pool
// surfaced prominently on the homepage AND at the top of the menu drawer /
// More dropdown. Each entry deep-links into the /learn-topics teaching
// card for its outcome so kids can jump straight to learning content.
//
// The pool for each tier now holds 8-12 objectives so we can rotate the
// visible 4 every 24 hours (via `keyObjectivesForAge`). Order stays
// deterministic across page reloads on the same day.

import { TOPICS } from "@/lib/topicContent";

/**
 * @typedef {Object} KeyObjective
 * @property {string} outcomeCode  Syllabus outcome code the teaching card unlocks
 * @property {string} subject      Compact subject id (used for /learn-topics?s=)
 * @property {string} tint         Tailwind bg class for the highlight card
 * @property {string} shortTitle   Kid-friendly headline
 * @property {string} tagline      One-line "why this matters" descriptor
 */

const OBJECTIVES = {
  "5-7": [
    { outcomeCode: "uk-ks1-geo-1", subject: "geography", tint: "bg-sky-300",
      shortTitle: "The 7 Continents & 5 Oceans",
      tagline: "Name every continent — the base of all geography." },
    { outcomeCode: "uk-ks1-ma-2",  subject: "maths", tint: "bg-amber-300",
      shortTitle: "Adding & Number Bonds to 20",
      tagline: "Fluent adding unlocks every future maths topic." },
    { outcomeCode: "uk-ks1-en-1",  subject: "english", tint: "bg-yellow-300",
      shortTitle: "Read by Sounding Out",
      tagline: "Phonics is the first step to reading anything." },
    { outcomeCode: "uk-ks1-sc-1",  subject: "science", tint: "bg-emerald-300",
      shortTitle: "Animals, Plants & Life",
      tagline: "Sort the living world — mammals, birds, fish, plants." },
    { outcomeCode: "uk-ks1-ma-1",  subject: "maths", tint: "bg-orange-300",
      shortTitle: "Counting to 100",
      tagline: "Numbers are how we describe the whole world." },
    { outcomeCode: "uk-ks1-sc-4",  subject: "science", tint: "bg-teal-300",
      shortTitle: "Solids, Liquids & Gases",
      tagline: "Water, ice and steam are all the same thing!" },
    { outcomeCode: "uk-ks1-hi-1",  subject: "history", tint: "bg-rose-300",
      shortTitle: "People Who Changed the World",
      tagline: "Explorers and inventors from long ago." },
    { outcomeCode: "uk-ks1-geo-3", subject: "geography", tint: "bg-violet-300",
      shortTitle: "Maps, Atlases & Globes",
      tagline: "See where you live and where the world is." },
    { outcomeCode: "uk-ks1-art-1", subject: "art", tint: "bg-pink-300",
      shortTitle: "Drawing & Painting",
      tagline: "Colours mix into a whole world of art." },
  ],
  "8-10": [
    { outcomeCode: "uk-ks2-geo-1", subject: "geography", tint: "bg-sky-300",
      shortTitle: "World Countries & Continents",
      tagline: "Locate any country on the map with confidence." },
    { outcomeCode: "uk-ks2-ma-3",  subject: "maths", tint: "bg-amber-300",
      shortTitle: "Times Tables to 12×12",
      tagline: "Unlocks division, fractions, area — everything." },
    { outcomeCode: "uk-ks2-sc-1",  subject: "science", tint: "bg-emerald-300",
      shortTitle: "Living Things & Food Chains",
      tagline: "How ecosystems work — sun → grass → rabbit → fox." },
    { outcomeCode: "uk-ks2-sc-4",  subject: "science", tint: "bg-violet-300",
      shortTitle: "The Solar System",
      tagline: "8 planets, 1 star, and how a year happens." },
    { outcomeCode: "uk-ks2-ma-4",  subject: "maths", tint: "bg-orange-300",
      shortTitle: "Fractions, Decimals & %",
      tagline: "Three ways to write parts of a whole." },
    { outcomeCode: "uk-ks2-en-2",  subject: "english", tint: "bg-yellow-300",
      shortTitle: "Writing Stories with Grammar",
      tagline: "Punctuation and tenses give writing power." },
    { outcomeCode: "uk-ks2-hi-1",  subject: "history", tint: "bg-rose-300",
      shortTitle: "Ancient Civilisations",
      tagline: "Egypt, Rome, China — where our world began." },
    { outcomeCode: "uk-ks2-sc-2",  subject: "science", tint: "bg-teal-300",
      shortTitle: "The Human Body",
      tagline: "How your bones, blood and brain work together." },
    { outcomeCode: "uk-ks2-cp-1",  subject: "ict", tint: "bg-slate-300",
      shortTitle: "Algorithms & Coding",
      tagline: "Give a computer clear step-by-step instructions." },
    { outcomeCode: "uk-ks2-ma-6",  subject: "maths", tint: "bg-fuchsia-300",
      shortTitle: "Shapes, Angles & Area",
      tagline: "The maths behind buildings, art and games." },
    { outcomeCode: "uk-ks2-pe-1",  subject: "pe", tint: "bg-lime-300",
      shortTitle: "Fitness & Fair Play",
      tagline: "Movement, teamwork and healthy habits." },
    { outcomeCode: "uk-ks2-art-1", subject: "art", tint: "bg-pink-300",
      shortTitle: "Drawing, Painting & Design",
      tagline: "See the world through an artist's eyes." },
  ],
  "11-15": [
    { outcomeCode: "uk-ks3-geo-5", subject: "geography", tint: "bg-sky-300",
      shortTitle: "Climate & Weather Systems",
      tagline: "Understand climate change — the story of our century." },
    { outcomeCode: "uk-ks3-ma-1",  subject: "maths", tint: "bg-amber-300",
      shortTitle: "Place Value & Number Systems",
      tagline: "From decimals to binary — the language of computers." },
    { outcomeCode: "uk-ks3-sc-1",  subject: "science", tint: "bg-emerald-300",
      shortTitle: "Biological Classification",
      tagline: "Domain → Kingdom → Species — how life is organised." },
    { outcomeCode: "uk-ks3-hi-1",  subject: "history", tint: "bg-rose-300",
      shortTitle: "Ancient Civilisations",
      tagline: "Mesopotamia, Egypt, Rome, China — how the world began." },
    { outcomeCode: "uk-ks3-ma-2",  subject: "maths", tint: "bg-orange-300",
      shortTitle: "Ratio, Proportion & Rates",
      tagline: "The algebra of everyday change and comparison." },
    { outcomeCode: "uk-ks3-sc-3",  subject: "science", tint: "bg-violet-300",
      shortTitle: "Forces, Waves & Energy",
      tagline: "Physics laws that run every technology on Earth." },
    { outcomeCode: "uk-ks3-geo-2", subject: "geography", tint: "bg-teal-300",
      shortTitle: "Plate Tectonics & Landforms",
      tagline: "Why continents move, mountains rise, volcanoes erupt." },
    { outcomeCode: "uk-ks3-en-1",  subject: "english", tint: "bg-yellow-300",
      shortTitle: "Analytical Reading",
      tagline: "Tone, structure, rhetoric — reading between the lines." },
    { outcomeCode: "uk-ks3-cp-1",  subject: "ict", tint: "bg-slate-300",
      shortTitle: "Programming Fundamentals",
      tagline: "Loops, conditionals, functions — the building blocks of code." },
    { outcomeCode: "uk-ks3-hi-2",  subject: "history", tint: "bg-fuchsia-300",
      shortTitle: "British History: Tudors to Today",
      tagline: "Reformation, Industry, Empire, World Wars." },
    { outcomeCode: "uk-ks3-sc-4",  subject: "science", tint: "bg-lime-300",
      shortTitle: "Working Scientifically",
      tagline: "Hypothesise, test, analyse — the scientific method." },
    { outcomeCode: "uk-ks3-art-1", subject: "art", tint: "bg-pink-300",
      shortTitle: "Art History & Movements",
      tagline: "From cave paintings to contemporary digital art." },
  ],
};

/** Deterministic day-of-year — same value across the whole day so the
 *  featured objectives don't churn every render, and every device shows
 *  the same set on the same date. */
function dayOfYear(date = new Date()) {
  const start = Date.UTC(date.getUTCFullYear(), 0, 0);
  const diff = Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()) - start;
  return Math.floor(diff / 86400000);
}

/**
 * Pick 4 rotating objectives for the child's tier.
 *
 * @param {number|null} age    Numeric age; drives tier selection.
 * @param {Date} [date]        Injectable for tests. Defaults to today.
 * @returns {Array<Object>}    4 enriched objectives (emoji, topic title, to).
 */
export function keyObjectivesForAge(age, date) {
  let tier = "8-10";
  if (age != null) {
    if (age <= 7) tier = "5-7";
    else if (age <= 10) tier = "8-10";
    else tier = "11-15";
  }
  const pool = OBJECTIVES[tier];
  // Rotate by day-of-year: skip N items and take next 4 (wrap-around).
  const offset = dayOfYear(date || new Date()) % pool.length;
  const picked = [];
  for (let i = 0; i < 4 && i < pool.length; i++) {
    picked.push(pool[(offset + i) % pool.length]);
  }
  // Enrich with the matching topic (emoji + tier intro) so the UI can
  // render a rich card without a second lookup.
  return picked.map((obj) => {
    const topic = TOPICS.find((t) =>
      t.outcomes.includes(obj.outcomeCode) || t.subject === obj.subject,
    );
    return {
      ...obj,
      tier,
      emoji: topic?.emoji || "🎯",
      topicTitle: topic?.title || obj.shortTitle,
      to: `/learn-topics?s=${obj.subject}#${obj.outcomeCode}`,
    };
  });
}

/**
 * Full pool for a tier — used by the "Start here today" heading to show
 * an "N of M" counter (e.g. "4 of 12 curriculum picks — new set tomorrow").
 */
export function keyObjectivesPoolSize(age) {
  let tier = "8-10";
  if (age != null) {
    if (age <= 7) tier = "5-7";
    else if (age <= 10) tier = "8-10";
    else tier = "11-15";
  }
  return OBJECTIVES[tier].length;
}
